import { h } from 'preact';
import { useRef, useEffect, useState } from 'preact/hooks';
import useFormStore from '../../store/useFormStore';
import { checkPromotionConditions } from '../../util/helper';

/**
 * Idle Popup Promotion Block
 * Displays a popup after 90 seconds of user inactivity
 * Shows "Before You Go" message with promo code
 */
export default function PromotionIdleBlock() {
  const currentFranchise = useFormStore((state) => state.currentFranchise);
  const currentScreen = useFormStore((state) => state.currentScreen);
  const manuallyEnteredPromoCodes = useFormStore((state) => state.manuallyEnteredPromoCodes);
  const addManualPromoCode = useFormStore((state) => state.addManualPromoCode);
  const appliedPromotions = useFormStore((state) => state.appliedPromotions);
  const cart = useFormStore((state) => state.cart);

  const dialogRef = useRef(null);
  const idleTimerRef = useRef(null);
  const [hasShownPopup, setHasShownPopup] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  // Get idle popup promotion
  const getIdlePromotion = () => {
    if (!currentFranchise?.promotions || currentFranchise.promotions.length === 0) {
      return null;
    }

    return currentFranchise.promotions.find(promo => {
      if (!promo.idle_popup) return false;

      // Check if date is valid
      if (promo.date) {
        const now = new Date();
        const startDate = promo.date.start ? new Date(promo.date.start) : null;
        const endDate = promo.date.end ? new Date(promo.date.end) : null;

        if (startDate && now < startDate) return false;
        if (endDate && now > endDate) return false;
      }

      // Check if conditions are met using helper
      if (!checkPromotionConditions(promo, cart)) {
        return false;
      }

      return true;
    });
  };

  const idlePromotion = getIdlePromotion();

  // Track user activity and show popup after 90 seconds of inactivity
  useEffect(() => {
    // Don't show on certain screens
    if (currentScreen === 'set-postal-code' || currentScreen === 'confirmation') {
      return;
    }

    // Don't show if no idle promotion
    if (!idlePromotion) {
      return;
    }

    // Don't show if already applied
    const isApplied =
      appliedPromotions.includes(idlePromotion.promo_code) ||
      manuallyEnteredPromoCodes.includes(idlePromotion.promo_code);

    if (isApplied) {
      return;
    }

    // Don't show if already shown this session
    if (hasShownPopup) {
      return;
    }

    const resetIdleTimer = () => {
      // Clear existing timer
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }

      // Set new timer for 90 seconds
      idleTimerRef.current = setTimeout(() => {
        if (dialogRef.current && !hasShownPopup) {
          dialogRef.current.showModal();
          setHasShownPopup(true);
        }
      }, 90000);
    };

    // Activity event listeners
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];

    activityEvents.forEach(event => {
      document.addEventListener(event, resetIdleTimer);
    });

    // Start initial timer
    resetIdleTimer();

    // Cleanup
    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      activityEvents.forEach(event => {
        document.removeEventListener(event, resetIdleTimer);
      });
    };
  }, [currentScreen, idlePromotion, hasShownPopup, appliedPromotions, manuallyEnteredPromoCodes]);

  const handleClose = () => {
    if (dialogRef.current) {
      dialogRef.current.close();
    }
  };

  const handleContinue = async () => {
    if (!idlePromotion) return;

    setIsApplying(true);

    try {
      // Add to manually entered codes
      addManualPromoCode(idlePromotion.promo_code);

      // Wait for cart validation to complete
      await new Promise(resolve => setTimeout(resolve, 100));

      // Close popup
      handleClose();
    } catch (error) {
      console.error('Error applying idle promo code:', error);
    } finally {
      setIsApplying(false);
    }
  };

  // No idle promotion to show
  if (!idlePromotion) {
    return null;
  }

  return (
    <dialog
      ref={dialogRef}
      className="backdrop:bg-black backdrop:opacity-70 rounded-lg shadow-xl max-w-[90vw] md:max-w-2xl w-full p-0 border-0"
    >
      <div className="relative bg-[#3a3a3a] text-white rounded-lg p-8 md:p-12">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center text-white hover:text-gray-300 transition-colors"
          aria-label="Close"
        >
          <svg
            className="w-8 h-8"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Content */}
        <div className="mb-8">
          <div
            className="prose prose-invert prose-lg max-w-none [&>*:last-child]:mb-0 [&_h2]:text-white [&_h2]:text-4xl [&_h2]:font-bold [&_h2]:mb-6 [&_h2]:underline [&_h2]:underline-offset-[1rem] [&_h2]:decoration-coit-yellow [&_p]:text-white [&_p]:text-xl [&_p]:leading-relaxed"
            dangerouslySetInnerHTML={{ __html: idlePromotion.text }}
          />
        </div>

        {/* Continue Button */}
        <div className="flex justify-center">
          <button
            onClick={handleContinue}
            disabled={isApplying}
            className={`coit-button ${
              isApplying ? 'opacity-50 cursor-wait' : ''
            }`}
          >
            {isApplying ? 'APPLYING...' : 'CONTINUE'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
